import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import config from "../config";
import { changeLanguage } from "../i18n";
import { isBinapsisBrand } from "../utils/utils";
import { apiJsonHeaders, withApiLocale } from "../utils/apiLocale";
import tr from "../i18n/translate";

export const STORAGE_KEY = "aristas.simple-auth.user";
const DEFAULT_API_BASE = "/api";

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const userIdRef = useRef(null);
  userIdRef.current = user?.id ?? null;

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem(STORAGE_KEY);
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        setUser(parsed);
      }
    } catch (error) {
      console.error("Error leyendo el usuario almacenado:", error);
      localStorage.removeItem(STORAGE_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  const persistUser = useCallback((profile) => {
    if (!profile) {
      localStorage.removeItem(STORAGE_KEY);
      setUser(null);
      return;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    setUser(profile);

    if (isBinapsisBrand() && profile.preferred_locale) {
      const loc = profile.preferred_locale === "en" ? "en" : "es";
      changeLanguage(loc);
    }
  }, []);

  const getBaseUrl = () => {
    return config?.baseUrlApi || DEFAULT_API_BASE;
  };

  const login = useCallback(
    async (identifier, password) => {
      try {
        const body = withApiLocale({
          username: identifier,
          email: identifier,
          password,
        });

        const response = await fetch(`${getBaseUrl()}/users/login`, {
          method: "POST",
          headers: apiJsonHeaders(),
          body: JSON.stringify(body),
        });

        let result = {};
        try {
          result = await response.json();
        } catch {
          // Ignoramos errores de parseo, manejados abajo
        }

        if (!response.ok || result?.error) {
          return {
            success: false,
            error:
              result?.error ||
              tr(
                "auth.invalidCredentials",
                "Credenciales inválidas. Intente nuevamente."
              ),
          };
        }

        persistUser(result);
        return { success: true };
      } catch (error) {
        console.error("Error durante el login simple:", error);
        return {
          success: false,
          error: tr(
            "auth.invalidCredentials",
            "Credenciales inválidas. Intente nuevamente."
          ),
        };
      }
    },
    [persistUser]
  );

  const logout = useCallback(() => {
    persistUser(null);
  }, [persistUser]);

  const switchUser = useCallback(
    async (userId) => {
      try {
        const response = await fetch(`${getBaseUrl()}/users/${userId}`);
        const result = await response.json();

        if (!response.ok || result?.error) {
          throw new Error(
            result?.error || "No se pudo obtener el usuario seleccionado."
          );
        }

        persistUser(result);
      } catch (error) {
        console.error("Error al cambiar de usuario:", error);
        throw error;
      }
    },
    [persistUser]
  );

  /** Relee el perfil desde el servidor (misma forma que login) sin pedir contraseña. */
  const refreshUser = useCallback(async () => {
    const id = userIdRef.current;
    if (!id) return false;
    try {
      const response = await fetch(`${getBaseUrl()}/users/${id}`);
      const result = await response.json();
      if (!response.ok || result?.error) {
        console.warn(
          "refreshUser:",
          result?.error || response.statusText || "error"
        );
        return false;
      }
      persistUser(result);
      return true;
    } catch (e) {
      console.error("Error al actualizar el usuario:", e);
      return false;
    }
  }, [persistUser]);

  // Profesional pendiente de habilitación: el admin actualiza la DB pero el cliente
  // seguía con el perfil cacheado; sincronizar sin cerrar sesión.
  useEffect(() => {
    if (!user?.id || user.role !== "doctor") return;

    const pending =
      user.is_professional_enabled === false ||
      user.is_professional_enabled === "false";

    const onVisible = () => {
      if (document.visibilityState === "visible") refreshUser();
    };

    document.addEventListener("visibilitychange", onVisible);
    let intervalId;
    if (pending) {
      refreshUser();
      intervalId = window.setInterval(refreshUser, 45_000);
    }

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [user?.id, user?.role, user?.is_professional_enabled, refreshUser]);

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, switchUser, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
